import {
    getApiErrorMessage,
    getFormMode,
    getRecordFromResponse,
    getResponseData,
    validateSchemaFields
} from 'lib/formUtils';
import {
    Box,
    Button,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import theme from 'theme';
import api from 'services/api';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { safeSetState } from 'lib/reactUtils';
import { AlertInformation } from 'components';
import { APESSOA_FISICA, APESSOA_JURIDICA, ANAO, ASIM } from 'consts';
import { ComboPadrao, FormActions, FormStatusSwitch, FormTextArea } from 'views/Components';
import StarIcon from '@material-ui/icons/Star';

/* Campos que seguem uppercase */
const UPPERCASE_FIELDS = [
    'cliente',
    'fantasia',
    'cnpjcpf',
    'rg',
    'orgaoemissor',
    'ie',
    'suframa',
    'cep',
    'logradouro',
    'numero',
    'bairro',
    'cxpostal',
    'complemento',
    'imunicipal',
    'fone',
    'fax',
    'celular',
    'nextel',
    'passaporte',
    'contato'
];

/* Campos que seguem lowercase */
const LOWERCASE_FIELDS = [
    'email',
    'site',
    'cadastur'
];

class ClienteForm extends Component {
    _isMounted = false;
    safeSetState = safeSetState;

    state = {
        cliente: {
            codcliente: '',
            cliente: '',
            tipopessoa: APESSOA_JURIDICA,
            inativo: ANAO,
            fantasia: '',
            dataconstituicao: '',
            cnpjcpf: '',
            rg: '',
            orgaoemissor: '',
            ie: '',
            suframa: '',
            cep: '',
            logradouro: '',
            numero: '',
            bairro: '',
            cxpostal: '',
            complemento: '',
            imunicipal: '',
            codtipocliente: '',
            fone: '',
            fax: '',
            celular: '',
            nextel: '',
            passaporte: '',
            codcidade: '',
            email: '',
            naopossuiemail: ANAO,
            site: '',
            perccomissao: '',
            cadastur: '',
            estrangeiro: ANAO,
            codvendedor: '',
            contato: '',
            obs: '',
            classificacao: 0
        },
        categorias: [],
        cidades: [],
        vendedores: [],
        paises: [],
        isLoading: true,
        isNewRecord: false,
        isView: false,
        openAlert: false,
        messageAlert: '',
        errors: {},
        schema: {
            cliente: {
                presence: {
                    allowEmpty: false,
                    message: 'Nome/Razão social obrigatório'
                },
                length: { maximum: 150 },
                type: 'string'
            }
        }
    };

    /* Carrega os dados iniciais da tela */
    async componentDidMount() {
        this._isMounted = true;

        const {
            id,
            isNewRecord,
            isView
        } = getFormMode(this.props.match, this.props.location);

        try {
            const [categorias, vendedores, paises] = await Promise.all([
                this.loadCategorias(),
                this.loadVendedores(),
                this.loadPaises()
            ]);

            if (isNewRecord) {
                const cidades = await this.loadCidades();

                this.safeSetState({
                    isNewRecord: true,
                    isView: false,
                    isLoading: false,
                    cliente: this.getClienteDefault(),
                    categorias,
                    vendedores,
                    paises,
                    cidades
                });

                return;
            }

            if (!isNaN(Number(id))) {
                await this.loadCliente(id, isView, categorias, vendedores, paises);
            }
        } catch (error) {
            console.error(
                'Erro ao carregar o cliente:',
                error.response?.data || error.message
            );

            this.handleOpenAlert(
                getApiErrorMessage(error, 'Erro ao carregar o cliente!')
            );

            this.safeSetState({ isLoading: false });
        }
    };

    /* Limpa a flag de montagem ao sair da tela */
    componentWillUnmount() {
        this._isMounted = false;
    };

    /* Retorna o cliente padrão */
    getClienteDefault = () => ({
        codcliente: '',
        cliente: '',
        tipopessoa: APESSOA_JURIDICA,
        inativo: ANAO,
        fantasia: '',
        dataconstituicao: '',
        cnpjcpf: '',
        rg: '',
        orgaoemissor: '',
        ie: '',
        suframa: '',
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cxpostal: '',
        complemento: '',
        imunicipal: '',
        codtipocliente: '',
        fone: '',
        fax: '',
        celular: '',
        nextel: '',
        passaporte: '',
        codcidade: '',
        email: '',
        naopossuiemail: ANAO,
        site: '',
        perccomissao: '',
        cadastur: '',
        estrangeiro: ANAO,
        codvendedor: '',
        contato: '',
        obs: '',
        classificacao: 0
    });

    /* Carrega as categorias de cliente */
    loadCategorias = async (codtipocliente) => {
        const url = codtipocliente
            ? `/tipocliente/listforchange/${codtipocliente}`
            : '/tipocliente/active/true';

        const response = await api.get(url);

        return getResponseData(response);
    };

    /* Carrega os vendedores */
    loadVendedores = async (codvendedor) => {
        const url = codvendedor ? `/vendedor/listforchange/${codvendedor}` : '/vendedor/active/true';
        const response = await api.get(url);

        return getResponseData(response);
    };

    /* Carrega as cidades */
    loadCidades = async (codcidade) => {
        const url = codcidade ? `/cidade/listforchange/${codcidade}` : '/cidade/active/true?limit=10000';
        const response = await api.get(url);
        const cidades = getResponseData(response);

        // monta a descrição já com a UF pro combo
        return cidades.map(cidade => ({
            ...cidade,
            desccidadeuf: cidade.sigla ? `${cidade.desccidade} (${cidade.sigla})` : cidade.desccidade
        }));
    };

    /* Carrega os países */
    loadPaises = async () => {
        const response = await api.get('/pais');

        return getResponseData(response);
    };

    /* Carrega os clientes */
    loadCliente = async (id, isView, categorias, vendedores, paises) => {
        const { history } = this.props;

        const response = await api.get(`/cliente/${id}`);
        const registro = getRecordFromResponse(response);

        if (!registro) {
            this.handleOpenAlert('Cliente não encontrado!');

            history.push(this.getBasePath());

            return;
        }

        const [categoriasAtualizadas, vendedoresAtualizados, cidades] = await Promise.all([
            registro.codtipocliente ? this.loadCategorias(registro.codtipocliente) : categorias,
            registro.codvendedor ? this.loadVendedores(registro.codvendedor) : vendedores,
            this.loadCidades(registro.codcidade)
        ]);

        this.safeSetState({
            cliente: {
                ...registro,
                tipopessoa: registro.tipopessoa || APESSOA_JURIDICA,
                dataconstituicao: registro.dataconstituicao || '',
                inativo: registro.inativo || ANAO,
                naopossuiemail: registro.naopossuiemail || ANAO,
                estrangeiro: registro.estrangeiro || ANAO
            },
            categorias: categoriasAtualizadas,
            vendedores: vendedoresAtualizados,
            cidades,
            paises,
            isNewRecord: false,
            isView,
            isLoading: false
        });
    };

    /* Evento onChange componentes */
    onChange = (event) => {
        const { name, type, value, checked } = event.target;

        let adjustedValue;

        if (type === 'checkbox') {
            adjustedValue = checked ? ANAO : ASIM;
        } else if (UPPERCASE_FIELDS.includes(name)) {
            adjustedValue = String(value).toUpperCase();
        } else if (LOWERCASE_FIELDS.includes(name)) {
            adjustedValue = String(value).toLowerCase();
        } else {
            adjustedValue = value;
        }

        this.setState(prevState => ({
            cliente: {
                ...prevState.cliente,
                [name]: adjustedValue
            }
        }));
    };

    /* Marca ou desmarca a opção "Não possui e-mail" */
    onChangeNaoPossuiEmail = (event) => {
        const checked = event.target.checked;

        this.setState(prevState => ({
            cliente: {
                ...prevState.cliente,
                naopossuiemail: checked ? ASIM : ANAO
            }
        }));
    };

    /* Marca ou desmarca a opção "Estrangeiro" */
    onChangeEstrangeiro = (event) => {
        const checked = event.target.checked;

        this.setState(prevState => ({
            cliente: {
                ...prevState.cliente,
                estrangeiro: checked ? ASIM : ANAO
            }
        }));
    };

    /* Abre a janela de alerta com mensagem */
    handleOpenAlert = (message) => {
        this.safeSetState({
            openAlert: true,
            messageAlert: message
        });
    };

    /* Fecha a janela de mensagem alerta */
    handleCloseAlert = () => {
        this.setState({ openAlert: false });
    };

    /* Retorna o UF e o país da cidade selecionada, só pra exibição */
    getUfPaisAtual = () => {
        const { cidades, cliente, paises } = this.state;

        const cidadeSelecionada = cidades.find(
            item => String(item.codcidade) === String(cliente.codcidade)
        );

        if (!cidadeSelecionada) {
            return {
                uf: '',
                pais: ''
            };
        }

        const paisSelecionado = paises.find(
            item => String(item.codpais) === String(cidadeSelecionada.codpais)
        );

        return {
            uf: cidadeSelecionada.sigla || '',
            pais: paisSelecionado ? paisSelecionado.nomepais : ''
        };
    };

    /* Retorna o payload de cliente */
    getPayloadCliente = () => {
        const { cliente } = this.state;

        return {
            cliente: cliente.cliente || '',
            tipopessoa: cliente.tipopessoa || APESSOA_JURIDICA,
            inativo: cliente.inativo || ANAO,
            fantasia: cliente.fantasia || '',
            dataconstituicao: cliente.dataconstituicao || '',
            cnpjcpf: cliente.cnpjcpf || '',
            rg: cliente.rg || '',
            orgaoemissor: cliente.orgaoemissor || '',
            ie: cliente.ie || '',
            suframa: cliente.suframa || '',
            cep: cliente.cep || '',
            logradouro: cliente.logradouro || '',
            numero: cliente.numero || '',
            bairro: cliente.bairro || '',
            cxpostal: cliente.cxpostal || '',
            complemento: cliente.complemento || '',
            imunicipal: cliente.imunicipal || '',
            codtipocliente: cliente.codtipocliente || '',
            fone: cliente.fone || '',
            fax: cliente.fax || '',
            celular: cliente.celular || '',
            nextel: cliente.nextel || '',
            passaporte: cliente.passaporte || '',
            codcidade: cliente.codcidade || '',
            email: cliente.email || '',
            naopossuiemail: cliente.naopossuiemail || ANAO,
            site: cliente.site || '',
            perccomissao: cliente.perccomissao || '',
            cadastur: cliente.cadastur || '',
            estrangeiro: cliente.estrangeiro || ANAO,
            codvendedor: cliente.codvendedor || '',
            contato: cliente.contato || '',
            obs: cliente.obs || '',
            classificacao: Number(cliente.classificacao) || 0,
        };
    };

    /* Clicar para salvar */
    onClickSave = async () => {
        const { cliente, isNewRecord } = this.state;
        const { history, loadCliente } = this.props;

        const errors = this.validationRequiredFields();
        this.setState({ errors });

        if (Object.keys(errors).length > 0) {
            this.handleOpenAlert('Verifique os campos obrigatórios!');

            return;
        }

        try {
            const payload = this.getPayloadCliente();

            if (isNewRecord) {
                await api.post('/cliente', payload);
            } else {
                await api.put(`/cliente/${cliente.codcliente}`, payload);
            }

            if (loadCliente) loadCliente();

            history.push(this.getBasePath());
        } catch (error) {
            console.error('Erro ao salvar:', error.response?.data || error.message);

            this.handleOpenAlert(
                getApiErrorMessage(error, 'Erro ao salvar!')
            );
        }
    };

    /* Clicar para cancelar */
    onClickCancel = () => {
        this.props.history.push(this.getBasePath());
    };

    /* Caminho base da tela */
    getBasePath = () => {
        const path = this.props.match?.path || '/crm/cliente/:id';

        return path.replace('/:id', '');
    };

    /* Valida os campos obrigatórios ao salvar */
    validationRequiredFields() {
        const { cliente, schema } = this.state;

        return validateSchemaFields(cliente, schema, ['cliente']);
    };

    onChangeClassificacao = (classificacao) => {
        this.setState(prevState => ({
            cliente: {
                ...prevState.cliente,
                classificacao
            }
        }));
    };

    render() {
        const {
            categorias,
            cidades,
            cliente,
            errors,
            isLoading,
            isView,
            messageAlert,
            openAlert,
            vendedores
        } = this.state;

        if (isLoading) {
            return (
                <CardContent>
                    <Typography variant="body1">
                        Carregando cliente...
                    </Typography>
                </CardContent>
            );
        }

        const isFisica = cliente.tipopessoa === APESSOA_FISICA;

        const {
            pais: paisAtual,
            uf: ufAtual
        } = this.getUfPaisAtual();

        return (
            <div>
                <CardContent>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ flexWrap: 'wrap' }}
                    >
                        <Typography
                            component="h1"
                            variant="h3"
                        >
                            Cliente {cliente.codcliente}
                        </Typography>

                        <FormStatusSwitch
                            checked={cliente.inativo !== ASIM}
                            name="inativo"
                            onChange={this.onChange}
                            disabled={isView}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        alignItems="flex-end"
                        justifyContent="space-between"
                        style={{
                            flexWrap: 'wrap',
                            gap: 12
                        }}
                    >
                        {/* Agrupamento à esquerda: Pessoa + Classificação */}
                        <Box
                            display="flex"
                            alignItems="flex-end"
                            style={{
                                gap: 12,
                                flexWrap: 'wrap'
                            }}
                        >
                            <fieldset
                                style={{
                                    padding: '6px 12px',
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    borderRadius: 12,
                                    flex: '0 1 auto',
                                    margin: 0
                                }}
                            >
                                <legend
                                    style={{
                                        fontSize: 12,
                                        fontFamily: 'Calibri, sans-serif',
                                        padding: '0 4px',
                                        color: theme.palette.grey[700]
                                    }}
                                >
                                    Pessoa
                                </legend>

                                <RadioGroup
                                    row
                                    name="tipopessoa"
                                    value={cliente.tipopessoa}
                                    onChange={this.onChange}
                                >
                                    <FormControlLabel
                                        value={APESSOA_FISICA}
                                        label={<span>Física</span>}
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                                disabled={isView}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        value={APESSOA_JURIDICA}
                                        label={<span>Jurídica</span>}
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                                disabled={isView}
                                            />
                                        }
                                    />
                                </RadioGroup>
                            </fieldset>

                            {/* Classificação dentro de um fieldset */}
                            <fieldset
                                style={{
                                    padding: '6px 12px',
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    borderRadius: 12,
                                    flex: '0 1 auto',
                                    margin: 0
                                }}
                            >
                                <legend
                                    style={{
                                        fontSize: 12,
                                        fontFamily: 'Calibri, sans-serif',
                                        padding: '0 4px',
                                        color: theme.palette.grey[700]
                                    }}
                                >
                                    Classificação
                                </legend>

                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ minHeight: 36 }}
                                >
                                    {[1, 2, 3, 4, 5].map(estrela => (
                                        <Button
                                            key={estrela}
                                            onClick={() =>
                                                this.onChangeClassificacao(estrela)
                                            }
                                            disabled={isView}
                                            title={`${estrela} estrela${estrela > 1 ? 's' : ''}`}
                                            style={{
                                                minWidth: 0,
                                                padding: 3,
                                                color:
                                                    estrela <= Number(cliente.classificacao || 0)
                                                        ? '#FBC02D'
                                                        : '#BDBDBD'
                                            }}
                                        >
                                            <StarIcon />
                                        </Button>
                                    ))}
                                </Box>
                            </fieldset>
                        </Box>

                        {/* Data permanece à direita */}
                        <TextField
                            label={isFisica ? 'Data de nascimento' : 'Data de constituição'}
                            name="dataconstituicao"
                            onChange={this.onChange}
                            type="date"
                            value={cliente.dataconstituicao || ''}
                            variant="outlined"
                            disabled={isView}
                            InputLabelProps={{ shrink: true }}
                            style={{
                                minWidth: 200,
                                flex: '0 1 200px'
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Nome / Razão social"
                            name="cliente"
                            value={cliente.cliente || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            helperText={errors.cliente || ''}
                            error={!!errors.cliente}
                            disabled={isView}
                            inputProps={{ maxLength: 150 }}
                            style={{
                                flex: 2,
                                minWidth: 260
                            }}
                        />

                        <TextField
                            label="Apelido / Nome fantasia"
                            name="fantasia"
                            value={cliente.fantasia || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label={isFisica ? 'CPF' : 'CNPJ'}
                            name="cnpjcpf"
                            value={cliente.cnpjcpf || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />

                        <TextField
                            label="RG"
                            name="rg"
                            value={cliente.rg || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView || !isFisica}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Órgão emissor"
                            name="orgaoemissor"
                            value={cliente.orgaoemissor || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView || !isFisica}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Passaporte"
                            name="passaporte"
                            value={cliente.passaporte || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView || !isFisica}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Inscrição estadual"
                            name="ie"
                            value={cliente.ie || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView || isFisica}
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />

                        <TextField
                            label="Inscrição municipal"
                            name="imunicipal"
                            value={cliente.imunicipal || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView || isFisica}
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />

                        <TextField
                            label="Suframa"
                            name="suframa"
                            value={cliente.suframa || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="CEP"
                            name="cep"
                            value={cliente.cep || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: '0 1 140px',
                                minWidth: 140
                            }}
                        />

                        <TextField
                            label="Logradouro"
                            name="logradouro"
                            value={cliente.logradouro || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 2,
                                minWidth: 240
                            }}
                        />

                        <TextField
                            label="Número"
                            name="numero"
                            value={cliente.numero || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 120
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Bairro"
                            name="bairro"
                            value={cliente.bairro || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />

                        <TextField
                            label="Complemento"
                            name="complemento"
                            value={cliente.complemento || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 200
                            }}
                        />

                        <TextField
                            label="Caixa postal"
                            name="cxpostal"
                            value={cliente.cxpostal || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <FormControl
                            variant="outlined"
                            disabled={isView}
                            style={{
                                flex: 2,
                                minWidth: 240
                            }}
                        >
                            <ComboPadrao
                                name={'codcidade'}
                                label={'Cidade'}
                                value={cliente.codcidade}
                                onChange={this.onChange}
                                fieldId={'codcidade'}
                                listItems={cidades}
                                fieldDescription={'desccidadeuf'}
                            />
                        </FormControl>

                        <TextField
                            label="UF"
                            value={ufAtual}
                            variant="outlined"
                            disabled
                            style={{
                                flex: '0 1 100px',
                                minWidth: 100
                            }}
                        />

                        <TextField
                            label="País"
                            value={paisAtual}
                            variant="outlined"
                            disabled
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <FormControl
                            variant="outlined"
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        >
                            <ComboPadrao
                                name={'codtipocliente'}
                                label={'Categoria de cliente'}
                                value={cliente.codtipocliente}
                                onChange={this.onChange}
                                fieldId={'codtipocliente'}
                                listItems={categorias}
                                fieldDescription={'desctipocliente'}
                            />
                        </FormControl>

                        <FormControl
                            variant="outlined"
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        >
                            <ComboPadrao
                                name={'codvendedor'}
                                label={'Vendedor'}
                                value={cliente.codvendedor}
                                onChange={this.onChange}
                                fieldId={'codvendedor'}
                                listItems={vendedores}
                                fieldDescription={'nomevendedor'}
                            />
                        </FormControl>
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Fone"
                            name="fone"
                            value={cliente.fone || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Fax"
                            name="fax"
                            value={cliente.fax || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Celular"
                            name="celular"
                            value={cliente.celular || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Nextel"
                            name="nextel"
                            value={cliente.nextel || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="E-mail"
                            name="email"
                            value={cliente.email || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView || cliente.naopossuiemail === ASIM}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />

                        <FormControlLabel
                            label="Não possui e-mail"
                            control={
                                <Checkbox
                                    checked={cliente.naopossuiemail === ASIM}
                                    onChange={this.onChangeNaoPossuiEmail}
                                    color="primary"
                                    disabled={isView}
                                />
                            }
                        />

                        <TextField
                            label="Site"
                            name="site"
                            value={cliente.site || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="% Comissão"
                            name="perccomissao"
                            value={cliente.perccomissao || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: '0 1 140px',
                                minWidth: 140
                            }}
                        />

                        <TextField
                            label="Cadastur"
                            name="cadastur"
                            value={cliente.cadastur || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 200
                            }}
                        />

                        <FormControlLabel
                            label="Estrangeiro"
                            control={
                                <Checkbox
                                    checked={cliente.estrangeiro === ASIM}
                                    onChange={this.onChangeEstrangeiro}
                                    color="primary"
                                    disabled={isView}
                                />
                            }
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <Tooltip title="Em breve">
                            <span>
                                <Button
                                    variant="outlined"
                                    disabled
                                    style={{ textTransform: 'none' }}
                                >
                                    % Desconto de serviço
                                </Button>
                            </span>
                        </Tooltip>

                        <Tooltip title="Em breve">
                            <span>
                                <Button
                                    variant="outlined"
                                    disabled
                                    style={{ textTransform: 'none' }}
                                >
                                    % Comissão de serviço
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    <br />

                    <Box>
                        <TextField
                            label="Contato principal"
                            name="contato"
                            value={cliente.contato || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            fullWidth
                        />
                    </Box>

                    <br />

                    <FormTextArea
                        label="Observação"
                        name="obs"
                        value={cliente.obs}
                        onChange={this.onChange}
                        disabled={isView}
                        minHeight={100}
                    />

                    <br />
                    <br />

                    <FormActions
                        onSave={this.onClickSave}
                        onCancel={this.onClickCancel}
                        disableSave={isView}
                    />
                </CardContent>

                <AlertInformation
                    open={openAlert}
                    message={messageAlert}
                    handleClose={this.handleCloseAlert}
                />
            </div>
        );
    }
};

ClienteForm.propTypes = {
    history: PropTypes.object,
    match: PropTypes.object,
    location: PropTypes.object,
    loadCliente: PropTypes.func
};

export default ClienteForm;